import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ROUTES } from '@/lib/constants';
import type { AppRole } from '@/types/auth';
import { isAdmin } from '@/types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
  requireApproval?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  requireApproval = true
}: ProtectedRouteProps) {
  const { user, session, isAuthReady, isApproved } = useAuth();
  const location = useLocation();

  // Wait until auth state is fully resolved (session + user data loaded, or confirmed logged out)
  if (!isAuthReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!session || !user) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check if user needs approval (employees only, admins auto-approved)
  if (requireApproval && !isApproved && !isAdmin(user.roles)) {
    return <Navigate to={ROUTES.PENDING} replace />;
  }

  // Check required roles
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = (user.roles || []).some(role => requiredRoles.includes(role));
    if (!hasRequiredRole) {
      // Redirect to dashboard if user doesn't have required role
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
  }

  return <>{children}</>;
}
